
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/toast';

export function useDbContentFavorites() {
  const [favorites, setFavorites] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();
  const { toast } = useToast();

  // Fetch favorites from database
  useEffect(() => {
    async function fetchFavorites() {
      if (!user) {
        setFavorites([]);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const { data, error } = await supabase
          .from('content_favorites')
          .select('content_id')
          .eq('user_id', user.id);

        if (error) {
          console.error('Error fetching favorites:', error);
          toast({
            title: 'Erro ao carregar favoritos',
            description: 'Não foi possível carregar seus conteúdos favoritos.',
            variant: 'destructive'
          });
          return;
        }

        if (data) {
          setFavorites(data.map(item => item.content_id));
        }
      } catch (error) {
        console.error('Error in favorites fetch:', error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchFavorites();
  }, [user, toast]);

  const addFavorite = async (contentId: string) => {
    if (!user) {
      toast({
        title: 'É necessário estar logado',
        description: 'Você precisa estar logado para adicionar favoritos.',
        variant: 'destructive'
      });
      return false;
    }

    try {
      const { error } = await supabase
        .from('content_favorites')
        .insert({ user_id: user.id, content_id: contentId });

      if (error) {
        console.error('Error adding favorite:', error);
        toast({
          title: 'Erro ao adicionar favorito',
          description: 'Não foi possível adicionar este conteúdo aos favoritos.',
          variant: 'destructive'
        });
        return false;
      }

      setFavorites(prev => [...prev, contentId]);
      return true;
    } catch (error) {
      console.error('Error in add favorite:', error);
      return false;
    }
  };

  const removeFavorite = async (contentId: string) => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('content_favorites')
        .delete()
        .eq('user_id', user.id)
        .eq('content_id', contentId);

      if (error) {
        console.error('Error removing favorite:', error);
        toast({
          title: 'Erro ao remover favorito',
          description: 'Não foi possível remover este conteúdo dos favoritos.',
          variant: 'destructive'
        });
        return false;
      }

      setFavorites(prev => prev.filter(id => id !== contentId));
      return true;
    } catch (error) {
      console.error('Error in remove favorite:', error);
      return false;
    }
  };

  const toggleFavorite = async (contentId: string) => {
    if (favorites.includes(contentId)) {
      return await removeFavorite(contentId);
    } else {
      return await addFavorite(contentId);
    }
  };

  const isFavorite = (contentId: string) => {
    return favorites.includes(contentId);
  };

  return {
    favorites,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    isFavorite,
    isLoading
  };
}
